// A multi-select filter dropdown with a search box.
//
// Replaces a native <select> in the Ledger Workspace's filter panel: a native
// select can't do multi-select in any usable way, can't be searched, and sizes
// itself to its longest option — which is what made a row of them come out
// ragged. This renders as a fixed-width select-alike and opens a checklist.
//
// The menu is portaled to <body> so it can escape the panel's bounds and any
// scrollable ancestor, the same reason SearchableSelect does it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { colors, spacing, fontSize, fontWeight, radius } from '../styles/theme';

// Sentinel for rows with no value in this column, so "show me the untagged
// ones" is expressible. Real values are compared as strings, so the leading
// space keeps it from colliding with anything a user would actually type.
export const BLANK_VALUE = ' (blanks)';

const labelOf = (v: string) => (v === BLANK_VALUE ? '(blanks)' : v);

// The menu is always at least this wide, however narrow its trigger is. The
// values in here are things like "KA 05 NP 4296 (Mahindra Bolero)" — at the
// trigger's width those all clipped to "KA 05 NP 4296 (Mahindra…", which made
// the list unusable for picking a vehicle.
const MENU_MIN_WIDTH = 320;

interface Props {
  /** Every selectable value, already in the order you want them listed. */
  options: string[];
  /** Ticked values. Empty means "no filter on this column". */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Shown in the search placeholder, e.g. "Subledger 2". */
  label: string;
}

export default function ColumnFilter({ options, selected, onChange, label }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0, width: MENU_MIN_WIDTH });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const active = selected.length > 0;
  const summary =
    selected.length === 0 ? 'All'
    : selected.length === 1 ? labelOf(selected[0])
    : `${selected.length} selected`;

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  // Position from the trigger's viewport rect, and keep it there while any
  // ancestor scrolls (capture:true catches inner scrollers, not just window).
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      // Never wider than the viewport, and never pushed off its left edge —
      // the wider menu would otherwise hang off screen for the last column.
      const width = Math.min(Math.max(r.width, MENU_MIN_WIDTH), window.innerWidth - 16);
      setCoords({
        top: r.bottom + 4,
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        width,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    // capture:true so Escape closes this menu before the workspace's own
    // handler sees it and starts closing the edit row instead.
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const shown = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(o => labelOf(o).toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        title={active ? selected.map(labelOf).join(', ') : `All ${label}`}
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '0.45rem 0.6rem',
          borderRadius: radius.sm,
          border: `1px solid ${active || open ? colors.primary : colors.border}`,
          background: active ? colors.primaryBg : colors.surface,
          color: active ? colors.primaryDark : colors.textSecondary,
          fontSize: fontSize.md,
          fontWeight: active ? fontWeight.semibold : fontWeight.medium,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          // Marks this as chrome that lives outside its owner in the DOM. The
          // workspace's click-away handler checks for it, so picking a value
          // isn't mistaken for clicking away from an open edit row.
          data-portal-dropdown="true"
          style={{
            position: 'fixed', top: coords.top, left: coords.left, width: coords.width,
            background: colors.surface, border: `1px solid ${colors.border}`,
            borderRadius: radius.md, boxShadow: '0 10px 24px rgba(15,23,42,0.18)',
            zIndex: 10040, display: 'flex', flexDirection: 'column',
          }}
        >
          <div style={{ padding: spacing(2), borderBottom: `1px solid ${colors.borderMuted}` }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${label}…`}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '0.3rem 0.45rem',
                border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                fontSize: fontSize.sm, outline: 'none', color: colors.textSecondary,
              }}
            />
          </div>

          <div style={{ maxHeight: 220, overflowY: 'auto', padding: `${spacing(1)} 0` }}>
            {shown.length === 0 && (
              <div style={{ padding: spacing(3), fontSize: fontSize.xs, color: colors.textFaint, textAlign: 'center' }}>
                No matches
              </div>
            )}
            {shown.map(value => (
              <label
                key={value}
                style={{
                  display: 'flex', alignItems: 'center', gap: spacing(2),
                  padding: `5px ${spacing(2)}`, cursor: 'pointer',
                  fontSize: fontSize.sm, color: colors.textSecondary,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={() => toggle(value)}
                  style={{ cursor: 'pointer', accentColor: colors.primary, flexShrink: 0 }}
                />
                <span
                  // Belt and braces for the longest values, which can still
                  // out-run even the widened menu.
                  title={labelOf(value)}
                  style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontStyle: value === BLANK_VALUE ? 'italic' : 'normal',
                    color: value === BLANK_VALUE ? colors.textFaint : undefined,
                  }}
                >
                  {labelOf(value)}
                </span>
              </label>
            ))}
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: spacing(3), padding: spacing(2),
            borderTop: `1px solid ${colors.borderMuted}`,
          }}>
            {/* This used to read "All", which sat next to "Select all" and read
                like a second way to tick everything — clicking it cleared the
                ticks instead, so it looked broken. It's the reset, so it says
                so. It still clears rather than ticking every value: an empty
                selection means "no filter", which stays correct when new values
                appear later, whereas ticking today's list would silently
                exclude tomorrow's. Clearing the search box too means the list
                visibly springs back to the full set, so the click reads. */}
            <button
              type="button"
              onClick={() => { onChange([]); setSearch(''); }}
              disabled={!active && !search.trim()}
              title="Clear this column's filter"
              style={{ ...linkBtn, ...(!active && !search.trim() ? linkBtnDisabled : {}) }}
            >
              Clear
            </button>
            {/* Adds to the selection instead of replacing it, so you can search
                twice and keep both sets — picking two vehicles used to mean the
                second search wiped the first pick. */}
            <button
              type="button"
              onClick={() => onChange(Array.from(new Set([...selected, ...shown])))}
              style={linkBtn}
            >
              {search.trim() ? 'Select matches' : 'Select all'}
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => setOpen(false)} style={linkBtn}>Done</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: colors.primary, cursor: 'pointer',
  fontSize: fontSize.xs, fontWeight: fontWeight.semibold, padding: 0, whiteSpace: 'nowrap',
};

const linkBtnDisabled: React.CSSProperties = {
  color: colors.textFaint, cursor: 'default',
};
